import produce from 'immer'
import Actions from '../actions'
import { getToken } from '../utils/fetchUtils'
import {
  DEFAULT_STATE,
  DEFAULT_SUPPORTED_STATES
} from '../consts/ProviderConsts'
import {
  normalizeDefaultState,
  normalizeSupportedStates,
  getDisplayBanners
} from '../utils/locationUtils'
import $t from '../i18n'

const initialState = {
  providers: [],
  providersCount: 0,
  providersNameList: [],
  providersResultsKey: null,
  specialityList: [],
  ipCity: null, // city of the user's IP address
  ipState: null, // state of the user's IP address
  ipZipCode: null, // zip code of the user's IP address,
  isProvidersLoading: false,
  isProvidersPrintingRequested: false,
  isSearchProvidersPdfRequested: false,
  selectedProvider: null,
  isProviderLoading: false, // note this loading flag for single provider
  isCareTeamLoading: false,
  telehealthPcp: null,
  physicalPcp: null,
  providerToken: null,
  isUpdatePcpLoading: false,
  pcpUpdateError: null,
  isChangeTelehealthPcpAvailbale: false,
  isSpecialiesLoading: false,
  isProviderNamesLoading: false,
  distanceFilterDefinition: {
    min: 1,
    max: 100,
    default: 50
  },
  // Search providers
  providersSuggestions: [],
  isProvidersSuggestionsLoading: false,
  providersSearchResults: [],
  selectedProviderDetails: null,
  isProviderDetailsLoading: false,
  providersSearchCount: 0,
  providersSearchDuration: 0,
  isProvidersSearchLoading: false,
  providersSearchEnableYearFilter: false,
  providersSearchResources: {
    external_links: [],
    popular_search_data: []
  },
  providersSearchQuery: null,
  // Seeded so the directory works on first render, before the app config arrives
  providersSupportedStates: DEFAULT_SUPPORTED_STATES,
  providersDefaultState: DEFAULT_STATE,
  providersSearchDisplayBanners: {},
  providersSearchLocation: {
    city: null,
    state: null,
    zipcode: null
  },
  providersSearchFilters: {
    language: null,
    gender: null,
    tier: null,
    providerType: null,
    acceptingNewPatients: null,
    distanceRadius: null,
    hospitalAffiliations: null,
    medicalGroupAffiliations: null,
    year: null
  },
  changePcpSelectedProviderId: null
}

export default produce((draft, action) => {
  const { payload } = action

  switch (action.type) {
    case Actions.IS_SIGNED_IN_RECEIVED: {
      const data = getToken()
      const token = data ? JSON.parse(data)?.token : null
      draft.providerToken = token
      break
    }
    case Actions.PROVIDERS_REQUESTED: {
      draft.isProvidersLoading = true
      draft.providersCount = 0
      draft.providersResultsKey = null
      break
    }
    case Actions.PROVIDERS_PRINT_REQUESTED: {
      draft.isProvidersPrintingRequested = true
      break
    }
    case Actions.PROVIDERS_PRINT_RECEIVED: {
      draft.isProvidersPrintingRequested = false
      break
    }
    case Actions.SEARCH_PROVIDERS_PDF_REQUESTED: {
      draft.isSearchProvidersPdfRequested = true
      break
    }
    case Actions.SEARCH_PROVIDERS_PDF_RECEIVED: {
      draft.isSearchProvidersPdfRequested = false
      break
    }
    case Actions.SPECIALITY_LIST_REQUESTED: {
      draft.isSpecialiesLoading = true
      break
    }
    case Actions.PROVIDERS_RECEIVED: {
      draft.isProvidersLoading = false
      draft.providers = payload?.providers
      draft.providersCount = payload?.count
      draft.providersResultsKey = payload?.key
      break
    }
    case Actions.VALIDATE_COUNTRY_RECEIVED: {
      draft.ipCity = payload?.location_city
      draft.ipState = payload?.location_state
      draft.ipZipCode = payload?.location_zipcode
      break
    }
    case Actions.PROVIDER_NAME_LIST_REQUESTED: {
      draft.isProviderNamesLoading = true
      break
    }
    case Actions.PROVIDER_NAME_LIST_RECEIVED: {
      draft.providersNameList = payload
      draft.isProviderNamesLoading = false
      break
    }
    case Actions.PROVIDER_NAME_LIST_RESET: {
      draft.providersNameList = []
      break
    }
    case Actions.SPECIALITY_LIST_RECEIVED: {
      draft.specialityList = payload
      draft.isSpecialiesLoading = false
      break
    }
    case Actions.PROVIDER_REQUESTED: {
      draft.selectedProvider = null
      draft.isProviderLoading = true
      break
    }
    case Actions.PROVIDER_DETAILS_REQUESTED: {
      draft.selectedProviderDetails = null
      draft.isProviderDetailsLoading = true
      break
    }
    case Actions.PROVIDER_DETAILS_RECEIVED: {
      draft.isProviderDetailsLoading = false
      draft.selectedProviderDetails = payload
      break
    }
    case Actions.PROVIDER_RECEIVED: {
      draft.isProviderLoading = false
      draft.selectedProvider = payload
      break
    }
    case Actions.PROVIDER_PCP_REQUESTED: {
      draft.isCareTeamLoading = true
      break
    }
    case Actions.PROVIDER_PCP_RECEIVED: {
      const pcpProviders = payload?.pcpProviders
      draft.telehealthPcp = pcpProviders?.find(
        provider => !!provider?.telehealth
      )
      draft.physicalPcp = pcpProviders?.filter(
        provider => !provider?.telehealth
      )
      draft.isChangeTelehealthPcpAvailbale = payload?.pcp_telehealth_flag
      draft.isCareTeamLoading = false
      break
    }
    case Actions.UPDATE_PCP_REQUESTED: {
      draft.isUpdatePcpLoading = true
      break
    }
    case Actions.UPDATE_PCP_RECEIVED: {
      draft.isUpdatePcpLoading = false
      break
    }
    case Actions.UPDATE_PCP_FAILED: {
      draft.pcpUpdateError = $t('alerts.pcpUpdateError')
      draft.isUpdatePcpLoading = false
      break
    }
    case Actions.CLEAR_ALERT: {
      draft.pcpUpdateError = null
      break
    }
    case Actions.PROVIDER_AUTH_RECEIVED: {
      draft.providerToken = payload?.token
      draft.telehealthPcp = payload?.telehealthPcp
      draft.physicalPcp = payload?.physicalPcp
      draft.isChangeTelehealthPcpAvailbale =
        payload?.isChangeTelehealthPcpAvailbale
      break
    }
    case Actions.SIGNOUT_RECEIVED: {
      draft.providerToken = null
      draft.telehealthPcp = null
      draft.physicalPcp = null
      draft.isChangeTelehealthPcpAvailbale = false
      break
    }
    case Actions.APP_CONFIG_RECEIVED: {
      draft.providersSearchEnableYearFilter =
        payload?.providersSearchEnableYearFilter || false
      const definition = payload?.distanceFilterDefinition
      if (!!definition) {
        draft.distanceFilterDefinition = {
          max: parseInt(definition?.max),
          min: parseInt(definition?.min),
          default: parseInt(definition?.default)
        }
      }
      if (payload?.providersSearchResources) {
        draft.providersSearchResources = payload.providersSearchResources
      }
      const supportedStates = normalizeSupportedStates(
        payload?.providersSearchSupportedStates
      )
      draft.providersSupportedStates = supportedStates
      draft.providersDefaultState = normalizeDefaultState(
        payload?.providersSearchSupportedStates,
        supportedStates
      )
      draft.providersSearchDisplayBanners = getDisplayBanners(
        payload?.providersSearchSupportedStates
      )
      break
    }
    case Actions.PROVIDERS_SEARCH_REQUESTED: {
      draft.isProvidersSearchLoading = true
      break
    }
    case Actions.PROVIDERS_SEARCH_RESULTS_RECEIVED: {
      draft.providersSearchResults = payload?.providers
      draft.providersSearchCount = payload?.search_metadata?.found || 0
      draft.providersSearchDuration =
        payload?.search_metadata?.search_time_ms || 0
      draft.isProvidersSearchLoading = false
      break
    }
    case Actions.PROVIDERS_SEARCH_FAILED: {
      draft.isProvidersSearchLoading = false
      break
    }
    case Actions.PROVIDERS_SUGGESTIONS_REQUESTED: {
      draft.isProvidersSuggestionsLoading = true
      break
    }
    case Actions.PROVIDERS_SUGGESTIONS_RESULTS_RECEIVED: {
      draft.providersSuggestions = payload
      draft.isProvidersSuggestionsLoading = false
      break
    }
    case Actions.PROVIDERS_SUGGESTIONS_FAILED: {
      draft.isProvidersSuggestionsLoading = false
      break
    }
    case Actions.SEARCH_BY_LOCATION_UPDATED: {
      draft.providersSearchLocation = payload
      break
    }
    case Actions.PROVIDERS_SEARCH_QUERY_UPDATED: {
      draft.providersSearchQuery = payload
      break
    }
    case Actions.SEARCH_PROVIDER_FILTERS_UPDATED: {
      draft.providersSearchFilters = payload
      break
    }
    case Actions.CHANGE_PCP_SELECTED_PROVIDER_ID: {
      draft.changePcpSelectedProviderId = payload?.providerId
      break
    }
    default: {
      // Nothing to do
      break
    }
  }
}, initialState)
